import {
  Stack,
  TextInput,
  Textarea,
  Flex,
  Text,
  Checkbox,
  Button,
  Tabs,
  Group,
  ActionIcon,
  Paper,
  Tooltip,
  LoadingOverlay,
  Collapse,
  Avatar,
} from "@mantine/core"
import { Suspense } from "react"
import {
  CancelButton,
  DeleteButtonWithConfirmModal,
  SubmitButton,
} from "../../../-components/Buttons"
import { DefaultList } from "../../../-components/default-list"
import { DefaultListItem } from "../../../-components/default-list/list-item"
import { FormButtonGroup } from "../../../-components/form-button-group"
import { UserAvatar } from "../../../-components/UserAvatar"
import { ScrollContainer } from "../../../-components/scroll-container"
import { PersonNameWithUserNameFallback } from "./ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { UseFormReturnType } from "@mantine/form"
import { ChatType, EmptyTreeNode } from "../../../orval/gen/model"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user"
import { DottedMenu } from "../../../-components/dotted-menu"
import {
  IconChevronDown,
  IconChevronUp,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useDebouncedValue } from "@mantine/hooks"
import {
  getPaginatedSimpleUsersWithPersonName,
  getSimpleUsersWithNames,
  useGetContractTypeTree,
  useGetProductionTree,
  useGetWorkAreaTree,
  getUserIdsByTreeNodes,
} from "@/orval/gen"
import { useQuery } from "@tanstack/react-query"
import { CreateChatMember, UserTypeFilter } from "@/orval/gen/model"
import { SingleSelectFromInfiniteQuery } from "@components/SelectFromQuery/SingleSelectFromInfiniteQuery"
import { ContractTypeTreeSelect } from "@components/tree/contract-type-tree-select"
import { ProductionTreeSelect } from "@components/production-tree-select"
import { DepartmentOrWorkareaTreeSelect } from "@components/department-workarea-tree-select"
import { t } from "i18next"
import { useChatAttachmentApi } from "@/attachment-api/chat-attachment-api"
import { useInvalidate } from "../../../typed-client/hooks/use-invalidate"
import {
  createChat,
  useGetChatMembers,
  getChatById,
} from "@/orval/gen/api/chat/chat"
import { useNavigate } from "@tanstack/react-router"
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../utils/form-submission-notifications"
import PickAndCropImageModal from "../../app/-components/pick-and-crop-image-modal"

interface AutoMembershipFilterGroupJson {
  productionNodes: string[]
  departmentNodes: string[]
  contractTypeNodes: string[]
  maySeeFullChat?: boolean
}

interface AutoMembershipFilterGroup extends AutoMembershipFilterGroupJson {
  id: number
  maySeeFullChat?: boolean
}

interface AutoMembershipFiltersJson {
  filters: AutoMembershipFilterGroupJson[]
}

let filterGroupIdCounter = 0
const getNextFilterGroupId = () => {
  filterGroupIdCounter += 1
  return filterGroupIdCounter
}

const toFilterGroup = (
  filter?: Partial<AutoMembershipFilterGroupJson>
): AutoMembershipFilterGroup => ({
  id: getNextFilterGroupId(),
  productionNodes: filter?.productionNodes ?? [],
  departmentNodes: filter?.departmentNodes ?? [],
  contractTypeNodes: filter?.contractTypeNodes ?? [],
  maySeeFullChat: filter?.maySeeFullChat ?? true,
})

const createEmptyFilterGroup = (): AutoMembershipFilterGroup => toFilterGroup()

const parseSavedFilters = (
  value?: string | null
): AutoMembershipFilterGroup[] => {
  if (!value) {
    return []
  }

  try {
    const parsed = JSON.parse(value) as AutoMembershipFiltersJson

    if (!parsed.filters?.length) {
      return []
    }

    return parsed.filters.map((filter) => toFilterGroup(filter))
  } catch {
    return []
  }
}

const hasFilterSelection = (filter: AutoMembershipFilterGroup) =>
  filter.productionNodes.length > 0 ||
  filter.departmentNodes.length > 0 ||
  filter.contractTypeNodes.length > 0

interface FormValues {
  chatId?: number
  name?: string | null
  description?: string | null
  chatMembers: Array<CreateChatMember>
  chatType: ChatType
  isReadOnly: boolean
  autoMembershipFilterJson?: string | null
  isHidden: boolean
  groupProfilePictureId?: number | null
  isAutoAddMembersEnabled?: boolean
  isAutoRemoveMembersEnabled?: boolean
}

interface Props {
  form: UseFormReturnType<FormValues>
  onSubmit: (values: FormValues) => void | Promise<void>
  onCancel: () => void
  onDelete?: () => Promise<void>
  isDeletePending?: boolean
  readonly?: boolean
  inactiveMemberUserIds?: number[]
  initialAutoMembershipFilterJson?: string | null
}

export function CreateUpdateChatForm({
  form,
  onSubmit,
  onCancel,
  onDelete,
  isDeletePending = false,
  readonly = false,
  inactiveMemberUserIds = [],
  initialAutoMembershipFilterJson,
}: Props) {
  const chatType = form.getValues().chatType
  const isUpdate = Object.keys(form.getValues()).includes("chatId")
  const [activeTab, setActiveTab] = React.useState<string | null>("baseData")
  const [showUserPicker, setShowUserPicker] = React.useState(false)

  const initialFilterGroups = parseSavedFilters(
    initialAutoMembershipFilterJson ?? form.getValues().autoMembershipFilterJson
  )
  const [filterGroups, setFilterGroups] =
    React.useState<AutoMembershipFilterGroup[]>(initialFilterGroups)
  const [collapsedFilterGroupIds, setCollapsedFilterGroupIds] = React.useState<
    number[]
  >(initialFilterGroups.map((group) => group.id))

  const [excludedPreviewUserIds, setExcludedPreviewUserIds] = React.useState<
    number[]
  >([])
  const [newMemberMaySeeFullChat, setNewMemberMaySeeFullChat] =
    React.useState<boolean>(true)
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false)
  const [previewUserIds, setPreviewUserIds] = React.useState<number[]>([])
  const isFeedType = chatType === "Feed" || chatType === "BroadcastFeed"
  const isBroadcastFeed = chatType === "BroadcastFeed"
  const isBroadcast = chatType === "Broadcast"
  const currentUserId = useGetAuthenticatedUser().data.userId
  const existingMemberIds = form.getValues().chatMembers.map((cm) => cm.userId)
  const existingMemberIdsKey = existingMemberIds.join(",")
  const inactiveMemberIdsKey = inactiveMemberUserIds.join(",")
  const existingMemberIdsSet = React.useMemo(
    () => new Set(existingMemberIds),
    [existingMemberIdsKey]
  )

  const chatAttachmentApi = useChatAttachmentApi()
  const invalidate = useInvalidate()
  const [uploading, setUploading] = React.useState(false)
  const [pendingProfileFile, setPendingProfileFile] =
    React.useState<File | null>(null)
  const [pendingProfilePreview, setPendingProfilePreview] = React.useState<
    string | null
  >(null)
  const [showPickModal, setShowPickModal] = React.useState(false)
  const navigate = useNavigate()
  const [pendingDelete, setPendingDelete] = React.useState(false)
  const handlePickUpload = React.useCallback(
    (file: File) => {
      if (pendingProfilePreview) {
        try {
          URL.revokeObjectURL(pendingProfilePreview)
        } catch {}
      }
      const url = URL.createObjectURL(file)
      setPendingProfileFile(file)
      setPendingProfilePreview(url)
      setPendingDelete(false)
      setShowPickModal(false)
    },
    [pendingProfilePreview]
  )
  const chatId = form.getValues().chatId
  const profilePictureId = form.getValues().groupProfilePictureId
  const profilePictureUrl =
    profilePictureId && chatId
      ? chatAttachmentApi.getThumbnailUrl(profilePictureId, chatId, 320, false)
      : null
  const inactiveMemberUserIdsSet = React.useMemo(
    () => new Set(inactiveMemberUserIds),
    [inactiveMemberIdsKey]
  )
  const adminOnlyType = ["Broadcast", "Feed", "BroadcastFeed"].includes(
    chatType
  )
  const tabPanelMinHeight = 360
  const membersScrollMaxHeight = 260

  const visiblePreviewUserIds = React.useMemo(
    () =>
      previewUserIds.filter(
        (userId) => !excludedPreviewUserIds.includes(userId)
      ),
    [excludedPreviewUserIds, previewUserIds]
  )

  const activeChatMembers = useGetChatMembers({ chatId, isActive: true })

  const [displayedMembers, setDisplayedMembers] = React.useState<
    { userId: number; isAdmin: boolean; maySeeFullChat: boolean }[]
  >([])

  React.useEffect(() => {
    if (displayedMembers.length === 0) {
      if (activeChatMembers.data && activeChatMembers.data.length > 0) {
        setDisplayedMembers(
          activeChatMembers.data.map((m) => ({
            userId: m.userId,
            isAdmin: (m.isAdmin ?? false) as boolean,
            maySeeFullChat: (m.lastChatPostBeforeJoiningId === null) as boolean,
          }))
        )
      } else {
        const initialFormMembers = (form.getValues().chatMembers ?? []).map(
          (cm) => ({
            userId: cm.userId,
            isAdmin: (cm.isAdmin ?? false) as boolean,
            maySeeFullChat: (cm.maySeeFullChat ?? true) as boolean,
          })
        )
        if (initialFormMembers.length > 0)
          setDisplayedMembers(initialFormMembers)
      }
    }
  }, [activeChatMembers.data])

  const isAutoAddMembersEnabled =
    form.getValues().isAutoAddMembersEnabled ?? false
  const isAutoRemoveMembersEnabled =
    form.getValues().isAutoRemoveMembersEnabled ?? false

  const handleSelectUser = (userId: number) =>
    !form.getValues().chatMembers.some((cm) => cm.userId === userId) &&
    chatType === "Direct"
      ? (function () {
          form.setFieldValue("chatMembers", [
            ...form
              .getValues()
              .chatMembers.filter((cm) => cm.userId === currentUserId),
            {
              userId: userId,
              isAdmin: false,
              maySeeFullChat: newMemberMaySeeFullChat,
              isAddedAutomatically: false,
            },
          ])
          setDisplayedMembers((prev) => {
            const withCurrent = prev.some((m) => m.userId === currentUserId)
              ? prev
              : [
                  ...prev,
                  {
                    userId: currentUserId,
                    isAdmin: false,
                    maySeeFullChat: true,
                  },
                ]
            if (withCurrent.some((m) => m.userId === userId)) return withCurrent
            return [
              ...withCurrent,
              {
                userId,
                isAdmin: false,
                maySeeFullChat: newMemberMaySeeFullChat,
              },
            ]
          })
        })()
      : null

  const autoMembershipFilterJson = React.useMemo(() => {
    const shouldSaveFilter =
      isAutoAddMembersEnabled || isAutoRemoveMembersEnabled
    if (!shouldSaveFilter) {
      return null
    }

    const activeFilters = filterGroups.filter(hasFilterSelection)
    if (activeFilters.length === 0) {
      return null
    }

    return JSON.stringify({
      filters: activeFilters.map(({ id: _id, ...filter }) => ({
        ...filter,
        maySeeFullChat: filter.maySeeFullChat ?? true,
      })),
    } satisfies AutoMembershipFiltersJson)
  }, [filterGroups, isAutoAddMembersEnabled, isAutoRemoveMembersEnabled])

  React.useEffect(() => {
    try {
      const current = form.getValues().autoMembershipFilterJson
      if (current !== autoMembershipFilterJson) {
        form.setFieldValue("autoMembershipFilterJson", autoMembershipFilterJson)
      }
    } catch {
      form.setFieldValue("autoMembershipFilterJson", autoMembershipFilterJson)
    }
  }, [autoMembershipFilterJson])

  const removePreviewUser = (userId: number) => {
    const existingIndex = form
      .getValues()
      .chatMembers.findIndex((cm) => cm.userId === userId)

    if (existingIndex >= 0) {
      form.removeListItem("chatMembers", existingIndex)
      setExcludedPreviewUserIds((prev) =>
        prev.includes(userId) ? prev : [...prev, userId]
      )
      return
    }

    setExcludedPreviewUserIds((prev) =>
      prev.includes(userId) ? prev : [...prev, userId]
    )
    setDisplayedMembers((prev) => prev.filter((m) => m.userId !== userId))
  }

  const toggleFilterGroupCollapsed = (filterGroupId: number) => {
    setCollapsedFilterGroupIds((prev) =>
      prev.includes(filterGroupId)
        ? prev.filter((id) => id !== filterGroupId)
        : [...prev, filterGroupId]
    )
  }

  React.useEffect(() => {
    if (!visiblePreviewUserIds || visiblePreviewUserIds.length === 0) return

    setDisplayedMembers((prev) => {
      const existingIds = new Set(prev.map((m) => m.userId))
      const toAdd = visiblePreviewUserIds
        .filter((id) => !existingIds.has(id))
        .filter((id) => !excludedPreviewUserIds.includes(id))
        .filter((id) => id !== currentUserId)
        .filter((id) => !inactiveMemberUserIdsSet.has(id))

      if (toAdd.length === 0) return prev

      const additions = toAdd.map((userId) => ({
        userId,
        isAdmin: isBroadcastFeed || isBroadcast,
        maySeeFullChat: true,
      }))
      return [...prev, ...additions]
    })
  }, [
    visiblePreviewUserIds.join(","),
    excludedPreviewUserIds.join(","),
    isBroadcastFeed,
    isBroadcast,
    currentUserId,
    inactiveMemberIdsKey,
  ])

  React.useEffect(() => {
    if (!activeChatMembers.data) return

    setDisplayedMembers((prev) => {
      const serverIds = new Set(activeChatMembers.data.map((m) => m.userId))
      const excluded = new Set(excludedPreviewUserIds)

      const formIds = new Set(
        form.getValues().chatMembers.map((cm) => cm.userId)
      )

      const kept = prev.filter(
        (m) =>
          serverIds.has(m.userId) ||
          formIds.has(m.userId) ||
          m.userId === currentUserId
      )

      const toAdd = activeChatMembers.data
        .filter(
          (m) =>
            !kept.some((k) => k.userId === m.userId) && !excluded.has(m.userId)
        )
        .map((m) => ({
          userId: m.userId,
          isAdmin: (m.isAdmin ?? false) as boolean,
          maySeeFullChat: (m.lastChatPostBeforeJoiningId === null) as boolean,
        }))

      if (toAdd.length === 0 && kept.length === prev.length) return prev
      return [...kept, ...toAdd]
    })
  }, [activeChatMembers.data, excludedPreviewUserIds.join(",")])

  React.useEffect(() => {
    if (!chatId) return
    void (async () => {
      try {
        await invalidate("/api/Chat/GetChatMembers", { chatId })
      } catch {}
      void activeChatMembers.refetch?.()
    })()
  }, [chatId])

  const addUserToMembers = (
    userId: number,
    isAdmin: boolean,
    maySeeFullChat: boolean = true
  ) => {
    const existingIndex = form
      .getValues()
      .chatMembers.findIndex((cm) => cm.userId === userId)

    if (existingIndex >= 0) {
      const existing = form.getValues().chatMembers[existingIndex]
      if (
        existing.isAdmin !== isAdmin ||
        existing.maySeeFullChat !== maySeeFullChat
      ) {
        form.replaceListItem("chatMembers", existingIndex, {
          userId,
          isAdmin,
          maySeeFullChat,
          isAddedAutomatically: existing.isAddedAutomatically ?? false,
        })
      }
      return
    }

    form.insertListItem("chatMembers", {
      userId,
      isAdmin,
      maySeeFullChat,
      isAddedAutomatically: false,
    })
    setExcludedPreviewUserIds((prev) => prev.filter((id) => id !== userId))
    setDisplayedMembers((prev) => {
      if (prev.some((m) => m.userId === userId)) {
        return prev.map((m) =>
          m.userId === userId ? { userId, isAdmin, maySeeFullChat } : m
        )
      }
      return [...prev, { userId, isAdmin, maySeeFullChat }]
    })
  }

  const updateFilterGroupNodes = (
    groupId: number,
    key: "productionNodes" | "departmentNodes" | "contractTypeNodes",
    newNodes: string[]
  ) => {
    setFilterGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, [key]: newNodes } : group
      )
    )
  }

  const removeMemberFromMembers = (userId: number) => {
    setDisplayedMembers((prev) => prev.filter((m) => m.userId !== userId))

    const index = form
      .getValues()
      .chatMembers.findIndex((cm) => cm.userId === userId)

    if (index >= 0) {
      form.removeListItem("chatMembers", index)
    }

    setExcludedPreviewUserIds((prev) =>
      prev.includes(userId) ? prev : [...prev, userId]
    )
  }

  const handleSelectMember = (userId: number) => {
    addUserToMembers(
      userId,
      isBroadcastFeed || isBroadcast,
      newMemberMaySeeFullChat
    )
    setShowUserPicker(false)
  }

  const handleValidatedSubmit = async (values: FormValues) => {
    if (chatType !== "Direct" && !values.name?.trim()) {
      form.setFieldError("name", t("RequiredField"))
      setActiveTab("baseData")
      return
    }

    form.clearFieldError("name")

    const existingUserIds = new Set(values.chatMembers.map((m) => m.userId))
    const previewMembersToAdd = displayedMembers
      .filter((m) => !existingUserIds.has(m.userId))
      .filter((m) => !inactiveMemberUserIdsSet.has(m.userId))
      .map((m) => ({
        userId: m.userId,
        isAdmin: m.isAdmin,
        maySeeFullChat: m.maySeeFullChat,
        isAddedAutomatically: true,
      }))

    const payload = {
      name: values.name ?? "",
      description: values.description ?? "",
      chatMembers: [...values.chatMembers, ...previewMembersToAdd],
      chatType: values.chatType,
      isReadOnly: values.isReadOnly,
      autoMembershipFilterJson: values.autoMembershipFilterJson ?? "",
      isHidden: values.isHidden,
      isAutoAddMembersEnabled: values.isAutoAddMembersEnabled ?? false,
      isAutoRemoveMembersEnabled: values.isAutoRemoveMembersEnabled ?? false,
    }

    if (!isUpdate) {
      try {
        const created = await createChat(payload)
        const createdChatId = created.chatId

        if (pendingProfileFile) {
          try {
            setUploading(true)
            await chatAttachmentApi.addProfilePictureForChat(
              pendingProfileFile,
              createdChatId
            )
            showSuccessNotification(t("ProfilePictureUploaded"))
            if (pendingProfilePreview) {
              try {
                URL.revokeObjectURL(pendingProfilePreview)
              } catch {}
            }
            setPendingProfileFile(null)
            setPendingProfilePreview(null)
          } catch (e) {
            showErrorNotification(e)
          } finally {
            setUploading(false)
          }
          try {
            const fresh = await getChatById({ chatId: createdChatId })
            form.setFieldValue(
              "groupProfilePictureId",
              fresh.groupProfilePictureId ?? null
            )
            invalidate("/api/Chat/GetChats")
          } catch {}
        }

        try {
          await invalidate("/api/Chat/GetChatMembers", {
            chatId: createdChatId,
          })
        } catch {}

        if (
          payload.chatType === "Feed" ||
          payload.chatType === "BroadcastFeed"
        ) {
          navigate({
            to: "/app/com/feeds/$feedId",
            params: { feedId: String(createdChatId) },
          })
          invalidate("/api/Chat/GetChats")
        } else {
          navigate({
            to: "/app/com/chat/$chatId",
            params: { chatId: String(createdChatId) },
          })
        }

        onCancel()
      } catch (e) {
        showErrorNotification(e)
      }
      return
    }

    if (previewMembersToAdd.length > 0) {
      await onSubmit({
        ...values,
        chatMembers: [...values.chatMembers, ...previewMembersToAdd],
      })
      try {
        const chatId = form.getValues().chatId as number

        try {
          await invalidate("/api/Chat/GetChatMembers", { chatId })
        } catch {}

        if (pendingDelete) {
          try {
            setUploading(true)
            await chatAttachmentApi.deleteProfilePictureForChat(chatId)
            invalidate("/api/Chat/GetChatById", { chatId })
            setPendingDelete(false)
          } catch (e) {
            showErrorNotification(e)
          } finally {
            setUploading(false)
          }
        }

        if (pendingProfileFile) {
          try {
            setUploading(true)
            await chatAttachmentApi.addProfilePictureForChat(
              pendingProfileFile,
              chatId
            )
            invalidate("/api/Chat/GetChatById", { chatId })
            if (pendingProfilePreview) {
              try {
                URL.revokeObjectURL(pendingProfilePreview)
              } catch {}
            }
            setPendingProfileFile(null)
            setPendingProfilePreview(null)
          } catch (e) {
            showErrorNotification(e)
          } finally {
            setUploading(false)
          }
        }

        if (pendingProfileFile) {
          try {
            const fresh = await getChatById({ chatId })
            form.setFieldValue(
              "groupProfilePictureId",
              fresh.groupProfilePictureId ?? null
            )
            invalidate("/api/Chat/GetChats")
          } catch {}
        }
      } catch (e) {
        showErrorNotification(e)
      }
      return
    }

    try {
      await onSubmit(values)

      const chatId = form.getValues().chatId as number

      try {
        await invalidate("/api/Chat/GetChatMembers", { chatId })
      } catch {}

      if (pendingDelete) {
        try {
          setUploading(true)
          await chatAttachmentApi.deleteProfilePictureForChat(chatId)
          invalidate("/api/Chat/GetChatById", { chatId })
          setPendingDelete(false)
        } catch (e) {
          showErrorNotification(e)
        } finally {
          setUploading(false)
        }
      }

      if (pendingProfileFile) {
        try {
          setUploading(true)
          await chatAttachmentApi.addProfilePictureForChat(
            pendingProfileFile,
            chatId
          )
          invalidate("/api/Chat/GetChatById", { chatId })
          if (pendingProfilePreview) {
            try {
              URL.revokeObjectURL(pendingProfilePreview)
            } catch {}
          }
          setPendingProfileFile(null)
          setPendingProfilePreview(null)
        } catch (e) {
          showErrorNotification(e)
        } finally {
          setUploading(false)
        }
      }

      if (pendingProfileFile) {
        try {
          const fresh = await getChatById({ chatId })
          form.setFieldValue(
            "groupProfilePictureId",
            fresh.groupProfilePictureId ?? null
          )
          invalidate("/api/Chat/GetChats")
        } catch {}
      }
    } catch (e) {
      showErrorNotification(e)
    }
  }

  const handleFormSubmit = React.useCallback(() => {
    form.onSubmit((values) => {
      handleValidatedSubmit(values)
    })()
  }, [form, handleValidatedSubmit])

  const { data: productionData } = useGetProductionTree()
  const { data: departmentData } = useGetWorkAreaTree()
  const { data: contractTypeData } = useGetContractTypeTree()

  const buildLabelMap = (
    treeNodes: EmptyTreeNode[] | undefined
  ): Record<string, string> => {
    const map: Record<string, string> = {}
    const walk = (nodes: EmptyTreeNode[]) => {
      nodes.forEach((node) => {
        if (node.value && node.label) map[node.value] = node.label
        if (node.children) walk(node.children)
      })
    }
    if (treeNodes) walk(treeNodes)
    return map
  }

  const productionLabelMap = React.useMemo(
    () => buildLabelMap(productionData?.tree),
    [productionData?.tree]
  )
  const departmentLabelMap = React.useMemo(
    () => buildLabelMap(departmentData?.tree),
    [departmentData?.tree]
  )
  const contractTypeLabelMap = React.useMemo(
    () => buildLabelMap(contractTypeData?.tree),
    [contractTypeData?.tree]
  )

  const getFilterGroupTitle = (filterGroup: AutoMembershipFilterGroup) => {
    const labels = [
      ...filterGroup.productionNodes.map(
        (value) => productionLabelMap[value] ?? value
      ),
      ...filterGroup.departmentNodes.map(
        (value) => departmentLabelMap[value] ?? value
      ),
      ...filterGroup.contractTypeNodes.map(
        (value) => contractTypeLabelMap[value] ?? value
      ),
    ]

    if (labels.length === 0) {
      return t("PleaseSelect")
    }

    return t("Group") + ` ${labels.join(", ")}`
  }

  const [debouncedFilterGroups] = useDebouncedValue(filterGroups, 500)

  React.useEffect(() => {
    let cancelled = false

    const fetchPreviewUsers = async () => {
      if (!debouncedFilterGroups || debouncedFilterGroups.length === 0) {
        setPreviewUserIds([])
        setIsPreviewLoading(false)
        return
      }

      setIsPreviewLoading(true)
      try {
        const groupPromises = debouncedFilterGroups.map(async (group) => {
          if (
            group.productionNodes.length === 0 &&
            group.departmentNodes.length === 0 &&
            group.contractTypeNodes.length === 0
          ) {
            return []
          }

          return getUserIdsByTreeNodes({
            workAreaNodeNames: group.departmentNodes,
            contractTypeNodeNames: group.contractTypeNodes,
            productionNodeNames: group.productionNodes,
          })
            .then((res) => res as unknown as number[])
            .catch(() => [])
        })

        const allResults = await Promise.all(groupPromises)

        if (cancelled) return

        const uniqueIds = Array.from(new Set(allResults.flat()))
          .filter((id) => id !== currentUserId)
          .filter((id) => !inactiveMemberUserIdsSet.has(id))
          .filter((id) => !existingMemberIdsSet.has(id)) as number[]

        setPreviewUserIds(uniqueIds)
      } catch (error) {
        console.error(error)
      } finally {
        if (!cancelled) {
          setIsPreviewLoading(false)
        }
      }
    }

    void fetchPreviewUsers()

    return () => {
      cancelled = true
    }
  }, [
    debouncedFilterGroups,
    currentUserId,
    inactiveMemberIdsKey,
    inactiveMemberUserIdsSet,
    existingMemberIdsKey,
    existingMemberIdsSet,
    isAutoAddMembersEnabled,
    isAutoRemoveMembersEnabled,
  ])

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        handleFormSubmit()
      }}
    >
      <Stack>
        {chatType === "Direct" ? (
          <>
            {!readonly && (
              <>
                <UserPicker
                  excludeIds={[
                    currentUserId,
                    ...form.getValues().chatMembers.map((cm) => cm.userId),
                  ]}
                  onSelect={handleSelectUser}
                />
              </>
            )}

            <ScrollContainer mah="40vh">
              <DefaultList
                flex={1}
                key={form.key("chatMembers")}
                data={form.getValues().chatMembers}
                renderItem={({ entity }) => (
                  <Suspense>
                    <DefaultListItem>
                      <UserAvatar userId={entity.userId} />
                      <PersonNameWithUserNameFallback userId={entity.userId} />
                      {entity.userId === currentUserId && ` (${t("You")})`}
                      {entity.isAdmin && ` (${t("Admin")})`}
                      <Flex flex="1" />
                    </DefaultListItem>
                  </Suspense>
                )}
              />
            </ScrollContainer>
          </>
        ) : (
          <Stack>
            {(chatType === ChatType.PrivateGroup ||
              chatType === ChatType.PublicGroup ||
              chatType === ChatType.Feed ||
              chatType === ChatType.Broadcast ||
              chatType === ChatType.BroadcastFeed) && (
              <Stack>
                <Group>
                  <Avatar
                    size={64}
                    src={
                      pendingProfilePreview ??
                      (pendingDelete ? undefined : profilePictureUrl) ??
                      undefined
                    }
                    style={{ cursor: readonly ? undefined : "pointer" }}
                    onClick={() => {
                      if (!readonly) setShowPickModal(true)
                    }}
                  />
                </Group>
                <PickAndCropImageModal
                  opened={showPickModal}
                  onClose={() => setShowPickModal(false)}
                  initialImageUrl={profilePictureUrl ?? null}
                  aspect={1}
                  onUpload={handlePickUpload}
                  onDelete={async () => {
                    setPendingProfileFile(null)
                    setPendingProfilePreview(null)
                    setPendingDelete(true)
                    setShowPickModal(false)
                  }}
                />
                <LoadingOverlay visible={uploading} />
              </Stack>
            )}
            <Tabs value={activeTab} onChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Tab value="baseData">{t("BaseData")}</Tabs.Tab>
                <Tabs.Tab value="members">
                  {isBroadcastFeed || isBroadcast ? t("Admins") : t("Members")}
                </Tabs.Tab>
                {!isBroadcastFeed && !isBroadcast && (
                  <Tabs.Tab value="groupSelection">
                    {t("GroupSelection")}
                  </Tabs.Tab>
                )}
              </Tabs.List>

              <Tabs.Panel
                value="baseData"
                pt="sm"
                style={{ minHeight: tabPanelMinHeight }}
              >
                <Stack>
                  <TextInput
                    required
                    disabled={readonly}
                    label={t("Name")}
                    key={form.key("name")}
                    {...form.getInputProps("name")}
                  />
                  <Textarea
                    disabled={readonly}
                    label={t("Description")}
                    key={form.key("description")}
                    {...form.getInputProps("description")}
                  />
                  {(chatType === "PrivateGroup" ||
                    chatType === "PublicGroup") && (
                    <Checkbox
                      disabled={readonly}
                      label={t("PublicGroup")}
                      description={t("PublicGroupDescription")}
                      checked={chatType === "PublicGroup"}
                      onChange={() =>
                        form.setFieldValue(
                          "chatType",
                          chatType === "PublicGroup"
                            ? "PrivateGroup"
                            : "PublicGroup"
                        )
                      }
                    />
                  )}
                  <Checkbox
                    label={t("ReadOnly")}
                    description={t("ReadOnlyDescription")}
                    disabled={readonly}
                    key={form.key("isReadOnly")}
                    {...form.getInputProps("isReadOnly", { type: "checkbox" })}
                  />
                  {isFeedType && (
                    <Checkbox
                      disabled={readonly}
                      label={t("BroadcastFeed")}
                      description={t("BroadcastFeedDescription")}
                      checked={isBroadcastFeed}
                      onChange={() =>
                        form.setFieldValue(
                          "chatType",
                          isBroadcastFeed ? "Feed" : "BroadcastFeed"
                        )
                      }
                    />
                  )}
                  <Checkbox
                    label={t("Hidden")}
                    description={t("HiddenFeedDescription")}
                    disabled={readonly}
                    key={form.key("isHidden")}
                    {...form.getInputProps("isHidden", { type: "checkbox" })}
                  />
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel
                value="members"
                pt="sm"
                style={{ minHeight: tabPanelMinHeight }}
              >
                <Stack h="100%" mih={0}>
                  <Group justify="space-between">
                    <Text fw={600}>
                      {isBroadcastFeed || isBroadcast
                        ? t("Admins")
                        : t("Members")}{" "}
                      ({displayedMembers.length})
                    </Text>
                    {!readonly && (
                      <Tooltip label={t("AddMemberTooltip")}>
                        <ActionIcon
                          variant="light"
                          onClick={() => setShowUserPicker((prev) => !prev)}
                        >
                          <IconPlus size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>

                  {showUserPicker && !readonly && (
                    <>
                      {" "}
                      {isUpdate && chatType !== ChatType.Feed && (
                        <Tooltip label={t("UserMaySeeFullChatDescription")}>
                          <Checkbox
                            label={t("UserMaySeeFullChat")}
                            checked={newMemberMaySeeFullChat}
                            onChange={(e) =>
                              setNewMemberMaySeeFullChat(
                                e.currentTarget.checked
                              )
                            }
                          />
                        </Tooltip>
                      )}
                      <UserPicker
                        excludeIds={[
                          currentUserId,
                          ...form
                            .getValues()
                            .chatMembers.map((cm) => cm.userId),
                        ]}
                        onSelect={handleSelectMember}
                      />
                    </>
                  )}

                  <ScrollContainer
                    flex={1}
                    mih={0}
                    mah={membersScrollMaxHeight}
                  >
                    <DefaultList
                      data={displayedMembers}
                      renderItem={({ entity }) => (
                        <Suspense>
                          <DefaultListItem>
                            <UserAvatar userId={entity.userId} />
                            <PersonNameWithUserNameFallback
                              userId={entity.userId}
                            />
                            {entity.userId === currentUserId &&
                              ` (${t("You")})`}
                            {entity.isAdmin && ` (${t("Admin")})`}
                            <Flex flex={1} />
                            {!readonly && entity.userId !== currentUserId && (
                              <DottedMenu
                                items={[
                                  ...(!adminOnlyType
                                    ? [
                                        {
                                          children: entity.isAdmin
                                            ? t("RemoveAdmin")
                                            : t("MakeAdmin"),
                                          onClick: () => {
                                            const memberIndex = form
                                              .getValues()
                                              .chatMembers.findIndex(
                                                (cm) =>
                                                  cm.userId === entity.userId
                                              )
                                            if (memberIndex >= 0) {
                                              form.replaceListItem(
                                                "chatMembers",
                                                memberIndex,
                                                {
                                                  userId: entity.userId,
                                                  isAdmin: !(
                                                    entity.isAdmin ?? false
                                                  ),
                                                  maySeeFullChat:
                                                    (form.getValues()
                                                      .chatMembers[memberIndex]
                                                      .maySeeFullChat as boolean) ??
                                                    true,
                                                  isAddedAutomatically:
                                                    (form.getValues()
                                                      .chatMembers[memberIndex]
                                                      .isAddedAutomatically as boolean) ??
                                                    false,
                                                }
                                              )
                                            } else {
                                              addUserToMembers(
                                                entity.userId,
                                                !!entity.isAdmin,
                                                true
                                              )
                                            }
                                          },
                                        },
                                      ]
                                    : []),
                                  ...(form
                                    .getValues()
                                    .chatMembers.find(
                                      (cm) => cm.userId === entity.userId
                                    )?.maySeeFullChat === false &&
                                  chatType !== "Feed"
                                    ? [
                                        {
                                          children: t("UserMaySeeFullChat"),
                                          onClick: () => {
                                            const memberIndex = form
                                              .getValues()
                                              .chatMembers.findIndex(
                                                (cm) =>
                                                  cm.userId === entity.userId
                                              )
                                            if (memberIndex >= 0) {
                                              form.replaceListItem(
                                                "chatMembers",
                                                memberIndex,
                                                {
                                                  userId: entity.userId,
                                                  isAdmin: (entity.isAdmin ??
                                                    false) as boolean,
                                                  maySeeFullChat: true,
                                                  isAddedAutomatically:
                                                    (form.getValues()
                                                      .chatMembers[memberIndex]
                                                      .isAddedAutomatically as boolean) ??
                                                    false,
                                                }
                                              )
                                            }
                                          },
                                        },
                                      ]
                                    : []),
                                  {
                                    children: t("Remove"),
                                    rightSection: <IconTrash />,
                                    onClick: () =>
                                      removeMemberFromMembers(entity.userId),
                                  },
                                ]}
                              />
                            )}
                          </DefaultListItem>
                        </Suspense>
                      )}
                    />
                  </ScrollContainer>
                </Stack>
              </Tabs.Panel>

              {!isBroadcastFeed && !isBroadcast && (
                <Tabs.Panel
                  value="groupSelection"
                  pt="sm"
                  style={{ minHeight: tabPanelMinHeight }}
                >
                  <Stack pos="relative">
                    <LoadingOverlay
                      visible={
                        isPreviewLoading && activeTab === "groupSelection"
                      }
                    />
                    <Group justify="space-between">
                      <Text fw={600}>{t("Groups")}</Text>
                      {!readonly && (
                        <Tooltip label={t("AddFilterTooltip")}>
                          <ActionIcon
                            variant="light"
                            onClick={() => {
                              const newFilterGroup = createEmptyFilterGroup()
                              setFilterGroups((prev) => [
                                ...prev,
                                newFilterGroup,
                              ])
                              setCollapsedFilterGroupIds((prev) => [
                                ...prev,
                                newFilterGroup.id,
                              ])
                            }}
                          >
                            <IconPlus size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>

                    {filterGroups.map((filterGroup) => (
                      <Paper key={filterGroup.id} withBorder p="sm" radius="md">
                        <Stack gap="xs">
                          <Group
                            justify="space-between"
                            align="flex-start"
                            wrap="nowrap"
                          >
                            <Text
                              size="sm"
                              fw={500}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {getFilterGroupTitle(filterGroup)}
                            </Text>
                            <Group
                              gap="xs"
                              align="flex-start"
                              wrap="nowrap"
                              style={{ flexShrink: 0 }}
                            >
                              <ActionIcon
                                variant="subtle"
                                onClick={() =>
                                  toggleFilterGroupCollapsed(filterGroup.id)
                                }
                              >
                                {collapsedFilterGroupIds.includes(
                                  filterGroup.id
                                ) ? (
                                  <IconChevronDown size={16} />
                                ) : (
                                  <IconChevronUp size={16} />
                                )}
                              </ActionIcon>
                              {!readonly && (
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  onClick={() => {
                                    setFilterGroups((prev) =>
                                      prev.filter(
                                        (group) => group.id !== filterGroup.id
                                      )
                                    )
                                    setCollapsedFilterGroupIds((prev) =>
                                      prev.filter((id) => id !== filterGroup.id)
                                    )
                                  }}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              )}
                            </Group>
                          </Group>

                          <Collapse
                            expanded={
                              !collapsedFilterGroupIds.includes(filterGroup.id)
                            }
                          >
                            <Stack gap="xs">
                              <ProductionTreeSelect
                                initialCheckedState={
                                  filterGroup.productionNodes
                                }
                                onCheckedStateChange={(values) =>
                                  updateFilterGroupNodes(
                                    filterGroup.id,
                                    "productionNodes",
                                    values
                                  )
                                }
                              />
                              <DepartmentOrWorkareaTreeSelect
                                initialCheckedState={
                                  filterGroup.departmentNodes
                                }
                                onCheckedStateChange={(values) =>
                                  updateFilterGroupNodes(
                                    filterGroup.id,
                                    "departmentNodes",
                                    values
                                  )
                                }
                              />
                              <ContractTypeTreeSelect
                                initialCheckedState={
                                  filterGroup.contractTypeNodes
                                }
                                onCheckedStateChange={(values) =>
                                  updateFilterGroupNodes(
                                    filterGroup.id,
                                    "contractTypeNodes",
                                    values
                                  )
                                }
                              />
                              {!readonly && chatType !== ChatType.Feed && (
                                <Tooltip
                                  label={t("UsersMaySeeFullChatDescription")}
                                >
                                  <Checkbox
                                    label={t("UsersMaySeeFullChat")}
                                    checked={filterGroup.maySeeFullChat ?? true}
                                    onChange={(e) => {
                                      const checked = e.currentTarget.checked
                                      setFilterGroups((prev) =>
                                        prev.map((g) =>
                                          g.id === filterGroup.id
                                            ? { ...g, maySeeFullChat: checked }
                                            : g
                                        )
                                      )
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </Collapse>
                        </Stack>
                      </Paper>
                    ))}
                    <Tooltip label={t("AddUsersAutomaticallyDescription")}>
                      <Checkbox
                        label={t("AddUsersAutomatically")}
                        disabled={readonly}
                        {...form.getInputProps("isAutoAddMembersEnabled", {
                          type: "checkbox",
                        })}
                      />
                    </Tooltip>
                    <Tooltip label={t("RemoveUsersAutomaticallyDescription")}>
                      <Checkbox
                        label={t("RemoveUsersAutomatically")}
                        disabled={readonly}
                        {...form.getInputProps("isAutoRemoveMembersEnabled", {
                          type: "checkbox",
                        })}
                      />
                    </Tooltip>
                    <ScrollContainer mah={220}>
                      <DefaultList
                        data={visiblePreviewUserIds.map((userId) => ({
                          userId,
                        }))}
                        renderItem={({ entity }) => (
                          <DefaultListItem>
                            <UserAvatar userId={entity.userId} />
                            <PersonNameWithUserNameFallback
                              userId={entity.userId}
                            />
                            {isBroadcastFeed &&
                              isBroadcast &&
                              ` (${t("Admin")})`}
                            <Flex flex={1} />
                            <DottedMenu
                              items={[
                                {
                                  children: t("Add") ?? "Add",
                                  onClick: () =>
                                    addUserToMembers(
                                      entity.userId,
                                      isBroadcastFeed || isBroadcast,
                                      true
                                    ),
                                },
                                {
                                  children: (t("Add") ?? "Add") + " (deny)",
                                  onClick: () =>
                                    addUserToMembers(
                                      entity.userId,
                                      isBroadcastFeed || isBroadcast,
                                      false
                                    ),
                                },
                                {
                                  children: t("Remove"),
                                  rightSection: <IconTrash />,
                                  onClick: () =>
                                    removePreviewUser(entity.userId),
                                },
                              ]}
                            />
                          </DefaultListItem>
                        )}
                      />
                    </ScrollContainer>
                  </Stack>
                </Tabs.Panel>
              )}
            </Tabs>
          </Stack>
        )}
        <FormButtonGroup>
          {readonly ? (
            <Button onClick={onCancel}>{t("Close")}</Button>
          ) : (
            <>
              {onDelete && isUpdate && (
                <DeleteButtonWithConfirmModal
                  confirmModalProps={{
                    onConfirm: async () => {
                      await onDelete()
                    },
                    confirmProps: { loading: isDeletePending },
                  }}
                />
              )}
              <CancelButton onClick={onCancel} />
              <SubmitButton>
                {isUpdate ? t("Update") : t("Create")}
              </SubmitButton>
            </>
          )}
        </FormButtonGroup>
      </Stack>
    </form>
  )
}

interface UserPickerProps {
  excludeIds: number[]
  onSelect: (userId: number) => void
}
function UserPicker({ excludeIds: excludeUserIds, onSelect }: UserPickerProps) {
  const [search, setSearch] = React.useState("")
  const [debouncedSearch] = useDebouncedValue(search, 500)

  const queryOptions = {
    userTypeFilter: UserTypeFilter.Users,
    excludeUserIds: excludeUserIds,
    searchString: debouncedSearch,
    filterInactive: true,
    includeUserIds: [],
  }

  const queryResult = useDefaultInfiniteQuery(
    getPaginatedSimpleUsersWithPersonName,
    (pageRequest) => ({
      ...pageRequest,
      ...queryOptions,
    }),
    {
      pageSize: 25,
    }
  )

  return (
    <SingleSelectFromInfiniteQuery
      required
      queryData={queryResult}
      entitiesToComboboxData={(data) => ({
        label: data.userName,
        value: data.userId.toString(),
      })}
      searchable
      placeholder={t("Users")}
      searchValue={search}
      onSearchChange={(v) => setSearch(v)}
      onSelectionChange={(v) => v && onSelect(parseInt(v))}
    />
  )
}
